import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  collection,
  deleteDoc,
  doc,
  updateDoc,
  addDoc,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore'
import { db, storage } from '../../plugins/firebase'
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes
} from 'firebase/storage'
import { notesValidator } from '../../utils/generalUtils.js'

// Async thunks
export const removeEntry = createAsyncThunk(
  'entryForm/removeEntry',
  async (entryId, { getState }) => {
    const { user } = getState()
    const entryRef = doc(db, 'flocks', user.userInfo.uid, 'entries', entryId)
    await deleteDoc(entryRef)
    return entryId
  }
)

export const toggleFoundation = createAsyncThunk(
  'entryForm/toggleFoundation',
  async ({ entryId, isFoundation }, { getState }) => {
    const { user } = getState()
    const entryRef = doc(db, 'flocks', user.userInfo.uid, 'entries', entryId)
    await updateDoc(entryRef, {
      isFoundation: !isFoundation,
      updatedAt: Timestamp.now()
    })
    return { entryId, isFoundation: !isFoundation }
  }
)

export const toggleFavorite = createAsyncThunk(
  'entryForm/toggleFavorite',
  async ({ entryId, isFavorite }, { getState }) => {
    const { user } = getState()
    const entryRef = doc(db, 'flocks', user.userInfo.uid, 'entries', entryId)
    await updateDoc(entryRef, {
      isFavorited: !isFavorite,
      updatedAt: Timestamp.now()
    })
    return { entryId, isFavorited: !isFavorite }
  }
)

export const saveEntry = createAsyncThunk(
  'entryForm/saveEntry',
  async (formData, { getState }) => {
    const { user, entryForm } = getState()
    const flockId = user.userInfo.uid

    if (entryForm.attachments.length > 0) {
      formData.photoIds = [crypto.randomUUID()]
    }

    const entriesCollectionRef = collection(db, 'flocks', flockId, 'entries')
    const { id: entryId } = await addDoc(entriesCollectionRef, {
      ...formData,
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp()
    })

    if (entryForm.attachments.length > 0) {
      await uploadImages(flockId, entryId, formData.photoIds[0], entryForm.attachments)
    }

    return entryId
  }
)

export const updateEntry = createAsyncThunk(
  'entryForm/updateEntry',
  async (entryId, { getState }) => {
    const { user, entryForm } = getState()
    const timestamp = Timestamp.now()
    const entry = entryForm.entries.find(e => e.entryId === entryId)
    
    if (!entry) return

    const copy = { ...entry }
    delete copy.imageUrlGetter

    // Handle notes
    if (copy.notes.active?.length > 0) {
      copy.notes.archived.push({
        timestamp,
        content: copy.notes.active,
        user: user.userInfo.displayName,
        userPhotoURL: user.userInfo.photoURL || 'https://randomuser.me/api/portraits/lego/1.jpg'
      })
      copy.notes.active = ''
    }

    const entryRef = doc(db, 'flocks', user.userInfo.uid, 'entries', entryId)
    await updateDoc(entryRef, {
      ...copy,
      updatedAt: timestamp
    })

    return { entryId, updatedEntry: copy }
  }
)

const uploadImages = async (flockId, entryId, uniqueId, attachments) => {
  const refPath = storageRef(storage, `${flockId}/${entryId}/${uniqueId}.jpg`)
  await uploadBytes(refPath, attachments[0])
}

const entryFormSlice = createSlice({
  name: 'entryForm',
  initialState: {
    formData: {
      notes: {
        active: '',
        archived: []
      }
    },
    entries: [],
    hasEntryChanged: false,
    editModeToggle: false,
    selectionIds: [],
    isDoneLoadingEntries: false,
    showBottomSheet: false,
    attachments: [],
    filterByFavoriteAndFoundation: false,
    isAppIniting: true,
    isFirebaseListenerActive: false,
    searchParams: '',
    loading: false,
    error: null,
  },
  reducers: {
    setEntries: (state, action) => {
      state.entries = action.payload
    },
    addEntry: (state, action) => {
      const entry = {
        ...action.payload,
        notes: notesValidator(action.payload.notes),
      }
      state.entries.push(entry)
    },
    updateEntryInState: (state, action) => {
      const { entryId, updatedData } = action.payload
      const index = state.entries.findIndex(e => e.entryId === entryId)
      if (index !== -1) {
        state.entries[index] = { ...state.entries[index], ...updatedData }
      }
    },
    removeEntryFromState: (state, action) => {
      state.entries = state.entries.filter(e => e.entryId !== action.payload)
    },
    setFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload }
    },
    clearFormData: (state) => {
      state.formData.notes.active = ''
      state.formData.notes.archived = []
    },
    setEditModeToggle: (state, action) => {
      state.editModeToggle = action.payload
    },
    setSelectionIds: (state, action) => {
      state.selectionIds = action.payload
    },
    setShowBottomSheet: (state, action) => {
      state.showBottomSheet = action.payload
    },
    setAttachments: (state, action) => {
      state.attachments = action.payload
    },
    setFilterByFavoriteAndFoundation: (state, action) => {
      state.filterByFavoriteAndFoundation = action.payload
    },
    setSearchParams: (state, action) => {
      state.searchParams = action.payload
    },
    setIsAppIniting: (state, action) => {
      state.isAppIniting = action.payload
    },
    setIsDoneLoadingEntries: (state, action) => {
      state.isDoneLoadingEntries = action.payload
    },
    setIsFirebaseListenerActive: (state, action) => {
      state.isFirebaseListenerActive = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(removeEntry.fulfilled, (state, action) => {
        state.entries = state.entries.filter(e => e.entryId !== action.payload)
      })
      .addCase(toggleFoundation.fulfilled, (state, action) => {
        const { entryId, isFoundation } = action.payload
        const entry = state.entries.find(e => e.entryId === entryId)
        if (entry) {
          entry.isFoundation = isFoundation
        }
      })
      .addCase(toggleFavorite.fulfilled, (state, action) => {
        const { entryId, isFavorited } = action.payload
        const entry = state.entries.find(e => e.entryId === entryId)
        if (entry) {
          entry.isFavorited = isFavorited
        }
      })
      .addCase(updateEntry.fulfilled, (state, action) => {
        const { entryId, updatedEntry } = action.payload
        const index = state.entries.findIndex(e => e.entryId === entryId)
        if (index !== -1) {
          state.entries[index] = { ...state.entries[index], ...updatedEntry }
        }
        state.editModeToggle = false
      })
  },
})

export const {
  setEntries,
  addEntry,
  updateEntryInState,
  removeEntryFromState,
  setFormData,
  clearFormData,
  setEditModeToggle,
  setSelectionIds,
  setShowBottomSheet,
  setAttachments,
  setFilterByFavoriteAndFoundation,
  setSearchParams,
  setIsAppIniting,
  setIsDoneLoadingEntries,
  setIsFirebaseListenerActive,
  clearError,
} = entryFormSlice.actions

// Selectors
export const selectEntries = (state) => state.entryForm.entries
export const selectFormData = (state) => state.entryForm.formData
export const selectEditModeToggle = (state) => state.entryForm.editModeToggle
export const selectSelectionIds = (state) => state.entryForm.selectionIds
export const selectShowBottomSheet = (state) => state.entryForm.showBottomSheet
export const selectAttachments = (state) => state.entryForm.attachments
export const selectFilterByFavoriteAndFoundation = (state) => state.entryForm.filterByFavoriteAndFoundation
export const selectSearchParams = (state) => state.entryForm.searchParams
export const selectIsDoneLoadingEntries = (state) => state.entryForm.isDoneLoadingEntries
export const selectDisableBottomSheetButton = (state) => state.entryForm.selectionIds.length !== 2

export const selectSearchedEntries = (state) => {
  const { entries, searchParams } = state.entryForm
  const query = searchParams?.toLowerCase() || ''
  const searchWords = query.split(/\s+/).filter(Boolean)
  
  if (!searchWords.length) return entries

  const matches = (value) => {
    if (value == null) return false
    if (Array.isArray(value)) return value.some(matches)
    if (typeof value === 'object') return Object.values(value).some(matches)
    return searchWords.every((word) =>
      String(value).toLowerCase().includes(word)
    )
  }

  return entries.filter((entry) => matches(entry))
}

export const selectFilteredEntriesBySex = (state) => (sex) => {
  const { entries, filterByFavoriteAndFoundation } = state.entryForm
  const valToUse = sex === 'mother' ? 'female' : 'male'
  const base = filterByFavoriteAndFoundation
    ? entries.filter((e) => e.isFoundation || e.isFavorited)
    : entries
  return base.filter((e) => e.sex === valToUse)
}

export default entryFormSlice.reducer